'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetAuthProvidersRow } from '@morfi/backend-sdk-v2/client';
import { CONNECTABLE_PROVIDERS, normalizeProviderName, type ProviderProfile, registerOtherAppleAudiences, SdkClientState, turnkeyAlreadyHoldsIdentity, useAuth, useAuthSync, useConnectProvider, useGetAuthProviders, useGoogleConnect, useOauthConnect, useSdkTurnkey, useUpdateProfile } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { Dispatch, JSX, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ConnectProviderDialog } from '@/components/settings/connect-provider-dialog';
import { getProviderDisplayName } from '@/components/settings/provider-display-name';
import { ProviderIcon } from '@/components/settings/provider-icon';
import { SocialProviderIcon } from '@/components/settings/social-provider-icon';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { CheckmarkCircle02Icon } from '@/components/ui/icon/glyphs';
import { TOAST_IDS } from '@/config/toast-ids';
import { runWithCleanup } from '@/lib/async/run-with-cleanup';
import { isLinkedElsewhereError } from '@/lib/auth/connect-error';
import { SETTINGS_AUTH_PROVIDERS_FIELDS } from '@/lib/sdk/projections/settings';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
type AuthProvider = GetAuthProvidersRow['providers'][number];

/**
 * Wait window for Turnkey to add a Google provider to the user's sub-org
 * after `handleAddOauthProvider({ openInPage: true })` returns from the
 * Safari redirect. If `useTurnkey().user.oauthProviders` doesn't include
 * the new Google entry within this window, we assume Turnkey rejected the
 * link (most likely cause: the Google identity is already in another
 * sub-org). Median Turnkey processing is ~1s, p95 ~4s; 12s gives slow
 * networks enough headroom while keeping the user's wait reasonable.
 */
const TRUST_TURNKEY_TIMEOUT_MS = 12_000;
export interface ProviderConnectResult {
  provider: string;
  imported: boolean;
  displayName: string | null;
  avatarUrl: string | null;
  handle: string | null;
}
interface ConnectProviderButtonsProps {
  /** Optional class name for the container. */
  className?: string;
  /** Callback after a provider is successfully connected. */
  onConnected?: (result: ProviderConnectResult) => void;
  /** Hide the connected provider pills (use when the parent renders its own connected list). */
  hideConnected?: boolean;
  /** Render larger, more prominent buttons (used in onboarding). */
  prominent?: boolean;
  /**
   * Restrict which providers are surfaced (intersected with `CONNECTABLE_PROVIDERS`).
   * When omitted, all connectable providers are shown. Used by surfaces that need
   * a single provider — e.g. the gift permalink card showing only "Connect X".
   */
  providers?: ReadonlyArray<string>;
}
interface ConnectProviderButtonsController {
  t: ReturnType<typeof useTranslations>;
  connectedDetails: ReadonlyArray<{
    provider: string;
    username: string | null;
    displayName: string | null;
    email: string | null;
    isSignup: boolean;
  }>;
  unconnected: readonly string[];
  connectingProvider: string | null;
  handleConnectClick: (provider: string) => Promise<void>;
  xEnrichDialogOpen: boolean;
  setXEnrichDialogOpen: Dispatch<SetStateAction<boolean>>;
  setXEnrichProfile: Dispatch<SetStateAction<ProviderProfile | null>>;
  xEnrichProfile: ProviderProfile | null;
  handleConfirmXEnrich: (importProfile: boolean, importHandle: boolean, suggestedHandle: string | null) => Promise<void>;
  xEnrichPending: boolean;
}

/**
 * The full OAuth connect flow (Google / X / Apple) — provider discovery,
 * popup + Safari-redirect resume, profile extraction, backend registration,
 * and the X-enrichment dialog state. Kept as a hook in this file (not a
 * separate module) so the load-bearing manual memos (handleXConnectResult /
 * handleGoogleResult / handleAppleConnected feed OAuth-result effect deps) and
 * the SSR-guarded Safari resume effect keep their file-keyed exemptions
 * (react-compiler-no-manual-memoization + FQ-140 baseline, set-state-in-effect).
 */
function useConnectProviderButtons({
  onConnected,
  providers: providersFilter
}: {
  onConnected?: ((result: ProviderConnectResult) => void) | undefined;
  providers?: ReadonlyArray<string> | undefined;
}): ConnectProviderButtonsController {
  const t = useTranslations('settings.connectedAccounts');
  const tX = useTranslations('auth.x_connect');
  const {
    user,
    setUser
  } = useAuth();
  const {
    mutateAsync: updateProfile
  } = useUpdateProfile();
  const providersQuery = useGetAuthProviders({
    fields: SETTINGS_AUTH_PROVIDERS_FIELDS
  });
  const providers: readonly AuthProvider[] = providersQuery.data?.providers ?? [];
  const {
    mutateAsync: connectProvider
  } = useConnectProvider();
  const {
    connectGoogle,
    finalizeGoogleConnect,
    finalizeGoogleConnectViaTurnkey
  } = useGoogleConnect({
    user,
    setUser,
    connectProvider,
    updateProfile
  });
  // Apple connect uses the generic Turnkey OAuth launcher (popup + redirect
  // resume). Like X, the identity is attached to the user's own Turnkey
  // sub-org before the Morfi row is written; the backend derives identity
  // from the JWKS-verified token. No email-match constraint (unlike Google).
  const {
    runOauthConnect
  } = useOauthConnect();
  const {
    peekPendingConnect,
    consumePendingConnect,
    connectFailure,
    clearConnectFailure
  } = useAuthSync();
  // After Safari redirect-return, we wait for Turnkey to expose a Google
  // entry in `useTurnkey().user.oauthProviders` and then call our backend
  // with its `subject` as `providerUserId`. The backend's
  // `findAuthProviderByProviderIdentity` cross-user check is the source
  // of truth for "this Google identity is already linked to another
  // Morfi user" — it runs regardless of whether the Turnkey-side entry
  // is freshly added or pre-existing (e.g. from a prior successful
  // attempt that didn't write to the Morfi DB). The watch effect picks
  // up ANY Google entry, not just newly added ones — Turnkey's own
  // dedup at the API level prevents the "wrong account" silently.
  const {
    user: turnkeyUser,
    refreshUser,
    addOauthProvider,
    httpClient,
    session,
    clientState
  } = useSdkTurnkey();

  // Attach the identity to the session's own sub-org so a later "Sign in with
  // X / Apple" resolves to these wallets. Two cases skip the attach and go
  // straight to the Morfi row, which the backend writes only after
  // re-verifying the token and its own cross-user check: this sub-org already
  // holds the identity (kept through an earlier disconnect), or Turnkey
  // rejects it as a duplicate because it lives on another sub-org (another
  // sub-org of this same user after a login rotation, or a different Morfi
  // account). Only the backend can tell those two apart: a duplicate held by a
  // different Morfi account surfaces as AUTH_PROVIDER_ALREADY_LINKED from
  // `/connect-provider`, which is the toast the user should see, not Turnkey's
  // raw rejection.
  const attachIdentityToSubOrg = useCallback(async (providerName: 'x' | 'apple', oidcToken: string): Promise<void> => {
    if (turnkeyAlreadyHoldsIdentity(turnkeyUser?.oauthProviders ?? [], providerName, oidcToken)) {
      logger.info(`${providerName}_connect.turnkey_identity_present_relinking`);
    } else {
      // The Turnkey registration is the link: a later sign-in with this
      // identity lands on the sub-org that holds the wallets only because it
      // is registered here. Turnkey rejects an identity another sub-org
      // already holds, and that rejection ends the connect: the person signs
      // in to that other account, exports its wallet, deletes it, then
      // connects here. The backend refuses the provider row without the
      // registration, so there is nothing to fall back to.
      await addOauthProvider({
        providerName,
        oidcToken
      });
    }
    // Apple's other audience is added on its own, also when the redirect
    // return already registered the token: the kit's addOauthProvider looks
    // every oidcClaims entry up first, a filter the auth proxy rejects.
    if (providerName === 'apple') {
      await registerOtherAppleAudiences({
        client: httpClient,
        completion: {
          sessionToken: session?.token
        },
        oidcToken,
        onAuthEvent: (_name: string, payload: Record<string, unknown>): void => {
          const log = payload.outcome === 'added' ? logger.info : logger.warn;
          log('turnkey.apple_audience_repair', {
            ...payload,
            platform: 'web',
            flow: 'connect'
          });
        }
      });
    }
  }, [addOauthProvider, httpClient, session?.token, turnkeyUser?.oauthProviders]);
  const pendingGoogleTurnkeyRef = useRef<{
    refreshed: boolean;
  } | null>(null);
  const [connectingProvider, setConnectingProvider] = useState<string | null>(null);
  // X enrichment dialog: opens after a successful X connect with the X
  // profile pulled from the just-written user_auth_providers row. The
  // dialog lets the user opt into using that data as their Morfi profile
  // (display name, avatar, handle). Confirm calls PATCH /v1/profile.
  const [xEnrichProfile, setXEnrichProfile] = useState<ProviderProfile | null>(null);
  const [xEnrichDialogOpen, setXEnrichDialogOpen] = useState<boolean>(false);
  const [xEnrichPending, setXEnrichPending] = useState<boolean>(false);
  const connectedTypes = new Set(providers.map(p => p.provider));
  const visibleProviders = providersFilter !== undefined ? CONNECTABLE_PROVIDERS.filter(p_0 => providersFilter.includes(p_0)) : CONNECTABLE_PROVIDERS;
  const unconnected = visibleProviders.filter(p_1 => !connectedTypes.has(p_1));

  /**
   * Surface the result of the backend-mediated X connect flow. Called both
   * inline (Chrome popup → postMessage) and on mount (Safari redirect →
   * `?x_connected=...` query params from x-connect-result).
   *
   * On success: invalidate the providers query, then refetch the latest list
   * so we can pull the just-written X row's profile data and open the
   * enrichment dialog. The user picks whether to import display name +
   * avatar + handle from X to their Morfi profile.
   */
  const handleXConnectResult = useCallback(async (result: {
    kind: 'success';
  }): Promise<void> => {
    if (result.kind === 'success') {
      showToast({
        id: TOAST_IDS.X_CONNECT_SUCCESS,
        message: tX('success'),
        variant: 'success'
      });

      // Refetch the providers list so we can read the new X row.
      // `.refetch()` resolves once the request completes, avoiding a race
      // with the dialog open below.
      try {
        const refreshed = await providersQuery.refetch();
        const xRow = refreshed.data?.providers.find(p_2 => p_2.provider === 'x');
        if (xRow !== undefined) {
          setXEnrichProfile({
            provider: 'x',
            username: xRow.providerUsername,
            displayName: xRow.providerDisplayName,
            // ALLOW-NSFW-STRIP: profile mutation input — NSFW comes from the moderation pipeline, not the user-supplied URL
            avatarUrl: xRow.providerAvatarUrl,
            email: xRow.providerEmail
          });
          setXEnrichDialogOpen(true);
        }
      } catch (err: unknown) {
        // Best-effort: if the cache lookup fails for any reason, the
        // success toast above is enough — connect already succeeded
        // server-side. Log so we can see if this ever happens in prod.
        logger.warn('Failed to read X provider after connect — enrichment dialog skipped', {
          error: err instanceof Error ? err.message : String(err)
        });
      }
      onConnected?.({
        provider: 'x',
        imported: false,
        displayName: null,
        avatarUrl: null,
        handle: null
      });
    }
  }, [providersQuery, tX, onConnected]);

  /**
   * Confirm handler for the X enrichment dialog. Calls PATCH /v1/me
   * with the user's chosen subset (display name + avatar from `importProfile`,
   * handle from `importHandle`). The X provider row itself is already in DB —
   * this only updates the user's primary Morfi profile fields.
   */
  const handleConfirmXEnrich = async (importProfile: boolean, importHandle: boolean, suggestedHandle: string | null): Promise<void> => {
    if (xEnrichProfile === null) return;
    setXEnrichPending(true);
    await runWithCleanup(async () => {
      try {
        const updates: {
          displayName?: string;
          avatarUrl?: string;
          handle?: string;
        } = {};
        if (importProfile && xEnrichProfile.displayName !== null) {
          updates.displayName = xEnrichProfile.displayName;
        }
        if (importProfile && xEnrichProfile.avatarUrl !== null) {
          updates.avatarUrl = xEnrichProfile.avatarUrl;
        }
        if (importHandle && suggestedHandle !== null && suggestedHandle.length > 0) {
          updates.handle = suggestedHandle;
        }
        if (Object.keys(updates).length > 0) {
          // IMPERSONATION_GUARD: connect-provider-buttons mounts in /settings (gated by SettingsImpersonationGate). Backend 403s as defence in depth.
          await updateProfile(updates);
          // Reflect the change immediately in local user state so the avatar /
          // name / handle update without a full reload.
          if (user !== null) {
            setUser({
              ...user,
              ...(updates.displayName !== undefined ? {
                displayName: updates.displayName
              } : {}),
              ...(updates.avatarUrl !== undefined ? {
                avatarUrl: updates.avatarUrl
              } : {}),
              ...(updates.handle !== undefined ? {
                handle: updates.handle
              } : {})
            });
          }
        }
        onConnected?.({
          provider: 'x',
          imported: importProfile,
          displayName: importProfile ? xEnrichProfile.displayName : null,
          avatarUrl: importProfile ? xEnrichProfile.avatarUrl : null,
          handle: importHandle ? suggestedHandle : null
        });
      } catch (err_0: unknown) {
        logger.error('Failed to import X profile to Morfi profile', {
          error: err_0 instanceof Error ? err_0.message.slice(0, 200) : String(err_0)
        });
        showToast({
          id: TOAST_IDS.PROFILE_ERROR,
          message: t('connectError'),
          variant: 'error',
          duration: 8000
        });
      }
    }, () => {
      setXEnrichPending(false);
      setXEnrichDialogOpen(false);
      setXEnrichProfile(null);
    });
  };
  const handleGoogleResult = useCallback((result_0: {
    success: boolean;
    mismatchEmail: string | null;
  }): void => {
    if (result_0.mismatchEmail !== null) {
      showToast({
        id: TOAST_IDS.PROVIDER_CONNECT_ERROR,
        message: t('googleEmailMismatch', {
          email: result_0.mismatchEmail
        }),
        variant: 'error',
        duration: 8000
      });
      return;
    }
    if (result_0.success) {
      showToast({
        id: TOAST_IDS.PROVIDER_CONNECTED,
        message: t('googleConnected'),
        variant: 'success'
      });
      onConnected?.({
        provider: 'google',
        imported: false,
        displayName: null,
        avatarUrl: null,
        handle: null
      });
    }
  }, [t, onConnected]);
  const handleAppleConnected = useCallback((): void => {
    showToast({
      id: TOAST_IDS.PROVIDER_CONNECTED,
      message: t('appleConnected'),
      variant: 'success'
    });
    onConnected?.({
      provider: 'apple',
      imported: false,
      displayName: null,
      avatarUrl: null,
      handle: null
    });
  }, [t, onConnected]);
  async function handleConnectClick(provider: string): Promise<void> {
    setConnectingProvider(provider);
    // Categorize a connect failure into the right toast. Takes the already-
    // extracted message string (callers log + pass it) so it carries no
    // `unknown` parameter. Used for both the explicit apple-error result and
    // any thrown error from the awaits — so the flow needs no `throw` inside
    // the try/catch (a pattern the React Compiler cannot lower in a component
    // body).
    const showConnectError = (errMsg: string): void => {
      const isCancelled = /cancel|closed|rejected|user denied/i.test(errMsg);
      if (isLinkedElsewhereError(errMsg)) {
        showToast({
          id: TOAST_IDS.PROVIDER_CONNECT_ERROR,
          message: t('providerAlreadyLinked'),
          variant: 'error',
          duration: 8000
        });
      } else if (errMsg.includes('PROVIDER_TYPE_ALREADY_CONNECTED') || errMsg.includes('already have')) {
        showToast({
          id: TOAST_IDS.PROVIDER_CONNECT_ERROR,
          message: t('providerTypeAlreadyConnected', {
            provider: getProviderDisplayName(provider)
          }),
          variant: 'error',
          duration: 8000
        });
      } else if (!isCancelled) {
        showToast({
          id: TOAST_IDS.PROVIDER_CONNECT_ERROR,
          message: t('connectError'),
          variant: 'error'
        });
      }
    };
    await runWithCleanup(async () => {
      try {
        if (provider === 'google') {
          const result_1 = await connectGoogle();
          if (result_1.redirecting) {
            // Page is navigating away to Google's OAuth screen; resume effect
            // will pick up after redirect-return.
            return;
          }
          handleGoogleResult(result_1);
          if (result_1.success) {
            await providersQuery.refetch();
          }
        } else if (provider === 'apple') {
          const result_2 = await runOauthConnect({
            provider: 'apple'
          });
          if (result_2.kind === 'redirecting') {
            // Safari/redirect mode — the pendingConnect resume effect finishes
            // the connect after the browser returns.
            return;
          }
          if (result_2.kind === 'cancelled') {
            return;
          }
          if (result_2.kind === 'error') {
            const errMsg_1 = result_2.error instanceof Error ? result_2.error.message : String(result_2.error);
            logger.warn('OAuth connect flow error', {
              provider,
              error: errMsg_1
            });
            showConnectError(errMsg_1);
            return;
          }
          // popup-success. Attach Apple to the user's OWN sub-org first, so a
          // later "Sign in with Apple" resolves to this account's wallets
          // instead of rotating the account onto a foreign sub-org; the
          // backend then re-verifies the OIDC token via JWKS.
          await attachIdentityToSubOrg('apple', result_2.oidcToken);
          await connectProvider({
            provider: 'apple',
            oidcToken: result_2.oidcToken
          });
          // Keep the button pending until the providers list reflects the
          // connect, so it cannot be launched again mid-reconciliation.
          await providersQuery.refetch();
          handleAppleConnected();
        } else if (provider === 'x') {
          // Turnkey-first X connect (mirrors the Google popup flow):
          // capture the Turnkey-issued OIDC token, attach X to the user's
          // OWN sub-org so a later "Sign in with X" resolves to the same
          // wallets, then write the Morfi DB row. The backend verifies the
          // token via its nonce = sha256(oauthPublicKey) binding to the
          // kit's per-attempt key. On Safari/mobile `runOauthConnect`
          // redirects via the login-shaped X flow and the pendingConnect
          // resume effect below finishes the connect on return.
          const result_3 = await runOauthConnect({
            provider: 'x'
          });
          if (result_3.kind === 'cancelled' || result_3.kind === 'redirecting') {
            return;
          }
          if (result_3.kind === 'error') {
            const errMsg_2 = result_3.error instanceof Error ? result_3.error.message : String(result_3.error);
            logger.warn('OAuth connect flow error', {
              provider,
              error: errMsg_2
            });
            showConnectError(errMsg_2);
            return;
          }
          await attachIdentityToSubOrg('x', result_3.oidcToken);
          await connectProvider({
            provider: 'x',
            oidcToken: result_3.oidcToken,
            oauthPublicKey: result_3.publicKey
          });
          await handleXConnectResult({
            kind: 'success'
          });
        }
      } catch (err_1: unknown) {
        const errMsg_0 = err_1 instanceof Error ? err_1.message : String(err_1);
        logger.warn('OAuth connect flow error', {
          provider,
          error: errMsg_0
        });
        // Turnkey's onError also records a duplicate rejection as
        // `connectFailure`; the toast below reports it, so consume it here or
        // the Google resume effect would replay it on the next connect.
        clearConnectFailure();
        showConnectError(errMsg_0);
      }
    }, () => setConnectingProvider(null));
  }

  // Redirect-return resume for Google/Apple/X via Turnkey. Google/Apple
  // land here from _callback-client's hash extraction; X lands here from
  // turnkey-provider's `morfiCompleteOauth` divert (the kit exchanges X's
  // code-flow return itself, so the token + per-attempt key arrive through
  // the bridge instead of the URL hash).
  useEffect(() => {
    const pending = peekPendingConnect();
    if (pending === null) return;
    const normalized = normalizeProviderName(pending.providerName);
    if (normalized === 'x') {
      consumePendingConnect();
      if (pending.oidcToken !== '' && typeof pending.oauthPublicKey === 'string') {
        const oauthPublicKey = pending.oauthPublicKey;
        void (async (): Promise<void> => {
          try {
            await attachIdentityToSubOrg('x', pending.oidcToken);
            await connectProvider({
              provider: 'x',
              oidcToken: pending.oidcToken,
              oauthPublicKey
            });
            await handleXConnectResult({
              kind: 'success'
            });
          } catch (err_2: unknown) {
            const errMsg_3 = err_2 instanceof Error ? err_2.message : String(err_2);
            logger.warn('X connect resume failed', {
              error: errMsg_3
            });
            clearConnectFailure();
            if (isLinkedElsewhereError(errMsg_3)) {
              showToast({
                id: TOAST_IDS.PROVIDER_CONNECT_ERROR,
                message: t('providerAlreadyLinked'),
                variant: 'error',
                duration: 8000
              });
            } else {
              showToast({
                id: TOAST_IDS.PROVIDER_CONNECT_ERROR,
                message: t('connectError'),
                variant: 'error'
              });
            }
          }
        })();
      }
    } else if (normalized === 'google') {
      consumePendingConnect();
      // Resolve the path:
      //  - Hash-extracted OIDC (Safari redirect): _callback-client parsed
      //    the id_token from window.location.hash and gave us providerUserId
      //    + a real oidcToken. Use the popup-style finalize so the backend
      //    gets the full OIDC payload AND the providerUserId for its
      //    cross-user check.
      //  - Trust-Turnkey fallback (no token, no providerUserId): only
      //    happens if hash extraction failed — backend rejects this case
      //    upfront for safety, surface a "could not connect" toast.
      //  - Popup capture (Chrome): `pending.oidcToken` is the captured
      //    token from `onOauthSuccess`; full path with rich metadata.
      const hasOidcToken = pending.oidcToken !== '';
      const hasProviderUserId = pending.providerUserId !== undefined && pending.providerUserId !== '';
      if (!hasOidcToken && !hasProviderUserId) {
        // Hash extraction failed AND no popup token. Last-resort fallback
        // to trust-Turnkey-with-poll. Rare in practice — only hits if
        // Safari stripped the hash for some reason.
        logger.warn('[turnkey] Google connect resume — no OIDC token and no providerUserId; deferring to Turnkey state poll');
        pendingGoogleTurnkeyRef.current = {
          refreshed: false
        };
        const trustTurnkeyTimeoutId = setTimeout((): void => {
          if (pendingGoogleTurnkeyRef.current === null) return;
          pendingGoogleTurnkeyRef.current = null;
          logger.warn('[turnkey] Google did not appear in oauthProviders within timeout — Turnkey rejection likely');
          showToast({
            id: TOAST_IDS.PROVIDER_CONNECT_ERROR,
            message: t('connectError'),
            variant: 'error',
            duration: 8000
          });
        }, TRUST_TURNKEY_TIMEOUT_MS);
        // Cancel the Turnkey-rejection fallback toast if this resume unmounts
        // (or re-runs) before the timeout fires, so it can't fire post-unmount.
        return (): void => clearTimeout(trustTurnkeyTimeoutId);
      }
      void (async (): Promise<void> => {
        try {
          const result_4 = hasOidcToken ? await finalizeGoogleConnect(pending.oidcToken) : await finalizeGoogleConnectViaTurnkey(pending.providerUserId);
          handleGoogleResult(result_4);
        } catch (err_3: unknown) {
          const errMsg_4 = err_3 instanceof Error ? err_3.message : String(err_3);
          logger.warn('Google connect resume failed', {
            error: errMsg_4
          });
          if (isLinkedElsewhereError(errMsg_4)) {
            showToast({
              id: TOAST_IDS.PROVIDER_CONNECT_ERROR,
              message: t('providerAlreadyLinked'),
              variant: 'error',
              duration: 8000
            });
          } else if (errMsg_4.includes('PROVIDER_TYPE_ALREADY_CONNECTED') || errMsg_4.includes('already have')) {
            showToast({
              id: TOAST_IDS.PROVIDER_CONNECT_ERROR,
              message: t('providerTypeAlreadyConnected', {
                provider: getProviderDisplayName('google')
              }),
              variant: 'error',
              duration: 8000
            });
          } else {
            showToast({
              id: TOAST_IDS.PROVIDER_CONNECT_ERROR,
              message: t('connectError'),
              variant: 'error'
            });
          }
        }
      })();
    } else if (normalized === 'apple') {
      // Back from the redirect the page mounts before the kit is ready: the
      // client, then the session and user arrive in that order, and attaching
      // the identity needs all three ("Client is not initialized"). Keep the
      // connect pending; this effect re-runs once the kit reports Ready.
      if (clientState !== SdkClientState.Ready) return;
      consumePendingConnect();
      if (pending.oidcToken !== '') {
        void (async (): Promise<void> => {
          try {
            await attachIdentityToSubOrg('apple', pending.oidcToken);
            await connectProvider({
              provider: 'apple',
              oidcToken: pending.oidcToken
            });
            handleAppleConnected();
          } catch (err_4: unknown) {
            const errMsg_5 = err_4 instanceof Error ? err_4.message : String(err_4);
            logger.warn('Apple connect resume failed', {
              error: errMsg_5
            });
            if (isLinkedElsewhereError(errMsg_5)) {
              showToast({
                id: TOAST_IDS.PROVIDER_CONNECT_ERROR,
                message: t('providerAlreadyLinked'),
                variant: 'error',
                duration: 8000
              });
            } else if (errMsg_5.includes('PROVIDER_TYPE_ALREADY_CONNECTED') || errMsg_5.includes('already have')) {
              showToast({
                id: TOAST_IDS.PROVIDER_CONNECT_ERROR,
                message: t('providerTypeAlreadyConnected', {
                  provider: getProviderDisplayName('apple')
                }),
                variant: 'error',
                duration: 8000
              });
            } else {
              showToast({
                id: TOAST_IDS.PROVIDER_CONNECT_ERROR,
                message: t('connectError'),
                variant: 'error'
              });
            }
          }
        })();
      }
    }
    return undefined;
  }, [peekPendingConnect, consumePendingConnect, finalizeGoogleConnect, finalizeGoogleConnectViaTurnkey, handleGoogleResult, connectProvider, handleAppleConnected, attachIdentityToSubOrg, handleXConnectResult, clearConnectFailure, clientState, t]);

  // Trust-Turnkey resume completion. After Turnkey processes
  // `handleAddOauthProvider` on redirect-return (priority #1
  // `onAddProvider` in completion.js), the user's sub-org should
  // include a Google entry. We watch `useTurnkey().user.oauthProviders`
  // for ANY Google entry and pass its `subject` to our backend as
  // `providerUserId` — the backend's
  // `findAuthProviderByProviderIdentity` does the real cross-user check.
  //
  // Edge cases handled:
  //  - Turnkey-side rejection (e.g. "Account already exists with this
  //    OIDC token" because Google is in another sub-org): consumed via
  //    `consumeConnectFailure()` set from turnkey-provider's onError.
  //  - Turnkey React state hasn't auto-refreshed after redirect-mode
  //    addOauthProvider (the SDK auto-refreshes for popup mode but the
  //    redirect-return path is less reliable): we call `refreshUser()`
  //    once to force a sync.
  useEffect(() => {
    const pending_0 = pendingGoogleTurnkeyRef.current;
    if (pending_0 === null) return;

    // Short-circuit: Turnkey reported an error during processing.
    // `connectFailure` is React state, so onError-driven updates trigger
    // a re-render that re-runs this effect — winning over the 12s timeout
    // even when the rejection arrives after the auth-callback has already
    // navigated us back here.
    if (connectFailure !== null) {
      pendingGoogleTurnkeyRef.current = null;
      logger.warn('[turnkey] Connect aborted by Turnkey rejection', {
        reason: connectFailure
      });
      showToast({
        id: TOAST_IDS.PROVIDER_CONNECT_ERROR,
        message: connectFailure === 'already-linked-elsewhere' ? t('providerAlreadyLinked') : t('connectError'),
        variant: 'error',
        duration: 8000
      });
      clearConnectFailure();
      return;
    }
    const oauthProviders = turnkeyUser?.oauthProviders;

    // Find any Google entry. Backend dedup is the source of truth for
    // "already linked to another Morfi user".
    const googleEntry = (oauthProviders ?? []).find(p_3 => p_3.providerName.toLowerCase().includes('google') && typeof p_3.subject === 'string' && p_3.subject !== '');
    if (googleEntry === undefined) {
      // Force one Turnkey refresh in case the SDK didn't auto-sync
      // after redirect-mode addOauthProvider. Subsequent watch fires
      // (driven by `turnkeyUser` updating) will see the entry.
      if (!pending_0.refreshed) {
        pendingGoogleTurnkeyRef.current = {
          refreshed: true
        };
        logger.info('[turnkey] Google not yet visible — forcing refreshUser()');
        void refreshUser().catch((err_5: unknown): void => {
          logger.warn('[turnkey] refreshUser failed', {
            error: err_5 instanceof Error ? err_5.message : String(err_5)
          });
        });
      }
      return;
    }
    const subject = googleEntry.subject;
    if (typeof subject !== 'string' || subject === '') return;
    pendingGoogleTurnkeyRef.current = null;
    void (async (): Promise<void> => {
      try {
        const result_5 = await finalizeGoogleConnectViaTurnkey(subject);
        handleGoogleResult(result_5);
      } catch (err_6: unknown) {
        const errMsg_6 = err_6 instanceof Error ? err_6.message : String(err_6);
        logger.warn('Google connect (trust-Turnkey) resume failed', {
          error: errMsg_6
        });
        if (isLinkedElsewhereError(errMsg_6)) {
          showToast({
            id: TOAST_IDS.PROVIDER_CONNECT_ERROR,
            message: t('providerAlreadyLinked'),
            variant: 'error',
            duration: 8000
          });
        } else if (errMsg_6.includes('PROVIDER_TYPE_ALREADY_CONNECTED') || errMsg_6.includes('already have')) {
          showToast({
            id: TOAST_IDS.PROVIDER_CONNECT_ERROR,
            message: t('providerTypeAlreadyConnected', {
              provider: getProviderDisplayName('google')
            }),
            variant: 'error',
            duration: 8000
          });
        } else {
          showToast({
            id: TOAST_IDS.PROVIDER_CONNECT_ERROR,
            message: t('connectError'),
            variant: 'error'
          });
        }
      }
    })();
  }, [turnkeyUser?.oauthProviders, connectFailure, clearConnectFailure, refreshUser, finalizeGoogleConnectViaTurnkey, handleGoogleResult, t]);

  // Build connected provider details for display
  const connectedDetails = visibleProviders.flatMap(p_4 => {
    if (!connectedTypes.has(p_4)) return [];
    const match = providers.find(prov => prov.provider === p_4);
    return [{
      provider: p_4,
      username: match?.providerUsername ?? null,
      displayName: match?.providerDisplayName ?? null,
      email: match?.providerEmail ?? null,
      isSignup: match?.isSignupProvider ?? false
    }];
  });
  return {
    t,
    connectedDetails,
    unconnected,
    connectingProvider,
    handleConnectClick,
    xEnrichDialogOpen,
    setXEnrichDialogOpen,
    setXEnrichProfile,
    xEnrichProfile,
    handleConfirmXEnrich,
    xEnrichPending
  };
}

/**
 * Reusable connect Google / X buttons with the full OAuth flow.
 * Handles provider discovery, OAuth popup, profile extraction,
 * confirmation dialog, and backend registration.
 *
 * Used in: profile settings, onboarding stage 2.
 */
export function ConnectProviderButtons(t0) {
  const $ = _c(28);
  const {
    className,
    onConnected,
    hideConnected: t1,
    prominent: t2,
    providers: providersFilter
  } = t0;
  const hideConnected = t1 === undefined ? false : t1;
  const prominent = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== onConnected || $[1] !== providersFilter) {
    t3 = {
      onConnected,
      providers: providersFilter
    };
    $[0] = onConnected;
    $[1] = providersFilter;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const c = useConnectProviderButtons(t3);
  const {
    t
  } = c;
  const t4 = className ?? "flex flex-col gap-2";
  let t5;
  if ($[3] !== c.connectedDetails || $[4] !== hideConnected || $[5] !== prominent) {
    t5 = !hideConnected && c.connectedDetails.map(cp => <div key={cp.provider} className={cn("flex items-center gap-2 rounded-control border border-border-primary bg-background-tertiary/30 px-3", prominent ? "h-12 text-body-lg" : "h-8")}>{prominent ? <SocialProviderIcon provider={cp.provider} className="size-4 shrink-0" /> : <ProviderIcon provider={cp.provider} className="size-4" />}<span className="text-body text-foreground-primary">{cp.username !== null ? `@${cp.username}` : cp.email !== null ? cp.email : getProviderDisplayName(cp.provider)}</span>{cp.displayName !== null && cp.username !== null && <span className="text-caption text-foreground-secondary">{cp.displayName}</span>}<Icon icon={CheckmarkCircle02Icon} className={cn("ml-auto text-feedback-success-content", prominent ? "size-5" : "size-4")} aria-hidden={true} /></div>);
    $[3] = c.connectedDetails;
    $[4] = hideConnected;
    $[5] = prominent;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== c || $[8] !== prominent || $[9] !== t) {
    t6 = prominent ? c.unconnected.map(provider => <AnimatedButton key={provider} variant="secondary" size="lg" className="w-full text-body-lg font-medium" disabled={c.connectingProvider !== null} loading={c.connectingProvider === provider} aria-label={`${t("connect")} ${getProviderDisplayName(provider)}`} onClick={() => {
      c.handleConnectClick(provider);
    }}>{c.connectingProvider === provider ? t("connectDialog.connecting") : t("connect")}{c.connectingProvider === provider ? null : <SocialProviderIcon provider={provider} className="size-4 shrink-0" />}</AnimatedButton>) : c.unconnected.length > 0 && <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:justify-end">{c.unconnected.map(provider_0 => <AnimatedButton key={provider_0} variant="secondary" className="w-full sm:w-auto" disabled={c.connectingProvider !== null} loading={c.connectingProvider === provider_0} aria-label={`${t("connect")} ${getProviderDisplayName(provider_0)}`} onClick={() => {
        c.handleConnectClick(provider_0);
      }}>{c.connectingProvider === provider_0 ? t("connectDialog.connecting") : t("connect")}{c.connectingProvider === provider_0 ? null : <SocialProviderIcon provider={provider_0} className="size-3.5 shrink-0" />}</AnimatedButton>)}</div>;
    $[7] = c;
    $[8] = prominent;
    $[9] = t;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = <div className={t4}>{t5}{t6}</div>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== c) {
    t8 = open => {
      c.setXEnrichDialogOpen(open);
      if (!open) {
        c.setXEnrichProfile(null);
      }
    };
    $[15] = c;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== c) {
    t9 = (importProfile, importHandle, suggestedHandle) => {
      c.handleConfirmXEnrich(importProfile, importHandle, suggestedHandle);
    };
    $[17] = c;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== c.xEnrichDialogOpen || $[20] !== c.xEnrichPending || $[21] !== c.xEnrichProfile || $[22] !== t8 || $[23] !== t9) {
    t10 = <ConnectProviderDialog open={c.xEnrichDialogOpen} onOpenChange={t8} profile={c.xEnrichProfile} onConnect={t9} isPending={c.xEnrichPending} />;
    $[19] = c.xEnrichDialogOpen;
    $[20] = c.xEnrichPending;
    $[21] = c.xEnrichProfile;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t7) {
    t11 = <>{t7}{t10}</>;
    $[25] = t10;
    $[26] = t7;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
}